import tpl from './button.tpl.html';
import linkTpl from './link.tpl.html';
import './new-button.component.scss';
import {noop} from "../../../app/common/common.utils";


/**
 * Choices for the button size:
 *
 * - normal: normal buttons
 * - minimal: smaller buttons
 * - large: bigger buttons
 * - massive: slightly bigger buttons than large, for confirmation buttons, main actions etc.
 * - icon: circle icon button, with this button the pixelSize argument must also be given
 *
 * @type {{MINIMAL: Symbol, NORMAL: Symbol, LARGE: Symbol, MASSIVE: Symbol, ICON: Symbol}}
 */
const SIZE_CHOICES = {
  SMALL: Symbol('small'),
  NORMAL: Symbol('normal'),
  LINK: Symbol('link')
};


/**
 * Choices for the button color.
 *
 * @type {{NONE: Symbol, PRIMARY: Symbol, SECONDARY: Symbol, PRIMARY_LIGHT: Symbol, SECONDARY_LIGHT: Symbol}}
 */
const COLOR_CHOICES = {
  NONE: Symbol('none'),
  PRIMARY: Symbol('primary'),
  SECONDARY: Symbol('secondary'),
  PRIMARY_LIGHT: Symbol('primarylight'),
  SECONDARY_LIGHT: Symbol('secondarylight'),
  SUBTLE: Symbol('subtle'),
  FIXED_COLORS: {
    GREEN: Symbol('green'),
    RED: Symbol('red'),
  }
};


/**
 * Get the color of the button based on its light and highlight status.
 * @param {boolean} light True if the button is on a dark background and should be light.
 * @param {boolean} secondary True if the button has the secondary color scheme
 * @param {boolean} subtle True if the button has subtle (grayish) style
 * @return {Symbol}
 */
function getColor(light, secondary, subtle = false, color = null) {
  if (color) {
    color = color.toUpperCase();
    const colorChoice = COLOR_CHOICES.FIXED_COLORS[color];

    if (colorChoice) {
      return colorChoice;
    }
  }

  if (light && !secondary) {
    return COLOR_CHOICES.PRIMARY_LIGHT;
  }
  else if (!secondary) {
    return COLOR_CHOICES.PRIMARY;
  }
  else if (light && secondary) {
    return COLOR_CHOICES.SECONDARY_LIGHT;
  }
  else if (secondary) {
    return COLOR_CHOICES.SECONDARY;
  }
  else if (subtle) {
    return COLOR_CHOICES.SUBTLE;
  }
  else {
    return COLOR_CHOICES.PRIMARY;
  }
}


class NewMDCButtonComponent {
  constructor($element) {

    this.$element = $element;

    this.SizeChoices = SIZE_CHOICES;
    this.ColorChoices = COLOR_CHOICES;

    /**
     * Is the button of secondary type
     * @type {boolean}
     */
    this.secondary = false;

    /**
     * Icon to show in the button
     * @type {string}
     */
    this.icon = null;

    /**
     * Size of the button, one of the size choices above
     * @type {Symbol}
     */
    this.size = SIZE_CHOICES.NORMAL;

    /**
     * Size of the button, one of the size choices above
     * @type {boolean}
     */
    this.block = false;

    /**
     * Color mode of the button, one of the choices above
     * @type {Symbol}
     */
    this.colorMode = COLOR_CHOICES.PRIMARY;

    /**
     * Is this button disabled?
     * @type {boolean}
     */
    this.isDisabled = false;

    /**
     * HTML type of the button element.
     * @type {string}
     */
    this.type = '';

    /**
     * The pixel size, only used if button size is ICON.
     * @type {number}
     */
    this.pixelSize = 0;

    /**
     * Does the button have no border at all
     * @type {boolean}
     */
    this.noBorder = false;

    /**
     * Callback to execute when button is clicked, if any.
     *
     * Not executed if button is disabled.
     *
     * @type {?function()}
     */
    this.clickAction = null;

    /**
     * Link for a-element href. If link is provided element will use link based template.
     *
     * @type {?string}
     */
    this.link = null;

    /**
     * Should the button look like a link?
     *
     * @type {boolean}
     */
    this.navLink = false;

    /**
     * Possible value for rel attribute for a-element.
     *
     * @type {?string}
     */
    this.linkRel = null;

    /**
     * Target of the link. Can use the defaul HTML5 targets: _self, _blank, _parent, _top.
     *
     * @type {?string}
     */
    this.target = '_self';

    this._customStyles = {};
    this._buttonElem = null;
    this._ripple = null;
    this._hoverListener = () => this._ripple.layout();

    // Classes to apply with ng-class
    this._classes = {};
  }

  $onInit() {
    if (this.type === '') {
      this.type = 'button';
    }

    if (this.link != null) {
      this.buttonType = 'a';
    }
    else {
      this.buttonType = 'button';
    }

    this.colorMode = this.getButtonColor();

    // Insert ripple to the actual clickable element
    this._buttonElem = this.$element.find(this.buttonType)[0];

    // Mobile safari fix: empty ontouch handler allows ripple to work.
    this._buttonElem.addEventListener('touchstart', noop, false);

    // MDCRipple doesn't work on IE10
    try {
      this._ripple = new window.mdc.MDCRipple(this._buttonElem);
    }
    catch(e) {
      // pass
    }

    // When button is hovered, re-layout ripple because it might be broken due to having display: none. Check this
    // issue:
    // https://github.com/material-components/material-components-web/issues/774#issuecomment-315082021
    this._buttonElem.addEventListener('mouseover', this._hoverListener, false);

    if (this.size === SIZE_CHOICES.ICON) {
      this._customStyles = {
        width: `${this.pixelSize}px`,
        height: `${this.pixelSize}px`,
        padding: 0,
        'border-radius': '50%'
      };
    }

    this._setClasses();
    this._setAttrs();
  }

  $onChanges() {
    this._setClasses();
  }

  $onDestroy() {
    this._buttonElem.removeEventListener('mouseover', this._hoverListener);
  }

  getButtonColor() {
    throw new Error('Implement the getButtonColor in child class');
  }

  _onClick() {
    if (!this.isDisabled && this.clickAction != null) {
      this.clickAction();
    }
  }

  // not that primary color has same set for light and normal theme
  _setClasses() {
    this._classes = {
      'mdc-button--small': this.size === this.SizeChoices.SMALL,
      'mdc-button--link': this.size === this.SizeChoices.LINK,
      'mdc-button--icon': this.size === this.SizeChoices.ICON,

      'mdc-button--primary': (this.colorMode === this.ColorChoices.PRIMARY || this.colorMode === this.ColorChoices.PRIMARY_LIGHT),
      'mdc-button--secondary': this.colorMode === this.ColorChoices.SECONDARY || this.navLink === true,
      'mdc-button--secondary-light': this.colorMode === this.ColorChoices.SECONDARY_LIGHT,

      'mdc-button--no-border': this.size === this.SizeChoices.LINK || this.noBorder === true || this.navLink === true,
      'mdc-button--block': this.block === true,
      'mdc-button--disabled': this.isDisabled === true,

      'mdc-button--green': this.colorMode === this.ColorChoices.FIXED_COLORS.GREEN,
      'mdc-button--red': this.colorMode === this.ColorChoices.FIXED_COLORS.RED,

      'mdc-button--nav-link': this.navLink === true,

      'new' : true,
    };
  }

  _setAttrs() {
    if (this.linkRel != null) {
      this._buttonElem.setAttribute('rel', this.linkRel);
    }
  }

  static componentConfig(controller) {
    return {
      templateUrl: buttonTemplateUrl,
      controller: controller,
      bindings: {
        icon: '@?',
        light: '<',
        secondary: '<?',
        isDisabled: '<?',
        block: '<?',
        type: '@?',
        pixelSize: '<?',
        noBorder: '<?',
        clickAction: '&?',
        link: '@?',
        linkRel: '@?',
        navLink: '<?',
        target: '@?',
        label: '@?',
        color: '@?'
      },
      transclude: true,
    };
  }
}

function buttonTemplateUrl($element, $attrs) {
  'ngInject';
  if ($attrs.link != null) {
    return linkTpl;
  } else {
    return tpl;
  }
}


export {
  NewMDCButtonComponent,
  SIZE_CHOICES, COLOR_CHOICES,
  getColor
};
